import { Match } from '../../types';
import { toDate } from '../matchdayService';
import {
  getFifaCodeFromApiTeam,
  getPairCodeFingerprint,
} from './theSportsDbTeamCodes';

const WORLD_CUP_EVENTS_URL =
  'https://www.thesportsdb.com/api/v1/json/123/eventsseason.php?id=4429';
const MUNDIAL_SEASON = '2026';
const FINISHED_STATUSES = new Set(['FT', 'AET', 'PEN']);

export interface SportsDbEvent {
  idEvent: string;
  strEvent: string;
  strHomeTeam: string;
  strAwayTeam: string;
  intHomeScore: string | null;
  intAwayScore: string | null;
  strStatus: string;
  strSeason: string;
  dateEvent: string;
  /** Fecha en zona del partido (CDMX); puede diferir de dateEvent en UTC. */
  dateEventLocal?: string;
  strGroup?: string;
}

export interface SyncMatchResult {
  match: Match;
  scoreA: number;
  scoreB: number;
  event: SportsDbEvent;
}

export type SyncResultStatus = 'pending' | 'already_saved' | 'unmatched' | 'invalid';

export interface SyncResultPreviewItem {
  event: SportsDbEvent;
  match: Match | null;
  scoreA: number | null;
  scoreB: number | null;
  status: SyncResultStatus;
  statusLabel: string;
}

export interface SyncWorldCupResultsSummary {
  updated: SyncMatchResult[];
  skipped: { event: SportsDbEvent; reason: string }[];
  unmatchedEvents: SportsDbEvent[];
  officialResults: SyncResultPreviewItem[];
}

function formatDateKey(value: Date | string): string {
  const date = value instanceof Date ? value : new Date(`${value}T12:00:00`);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Fechas candidatas del evento API (UTC + local del estadio). */
function getEventDateKeys(event: SportsDbEvent): Set<string> {
  const keys = new Set<string>();
  if (event.dateEvent) keys.add(event.dateEvent);
  if (event.dateEventLocal) keys.add(event.dateEventLocal);
  return keys;
}

function parseScore(value: string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const score = Number(value);
  if (!Number.isFinite(score) || score < 0) return null;
  return score;
}

function matchesScheduleCriteria(
  match: Match,
  eventDateKeys: Set<string>,
  eventGroup?: string
): boolean {
  if (!match.team_b) return false;

  const matchDay = formatDateKey(toDate(match.date));
  const sameDay = eventDateKeys.has(matchDay);
  const sameGroup =
    !eventGroup || !match.group || match.group.toUpperCase() === eventGroup;

  return sameDay && sameGroup;
}

function pickBestCandidate(candidates: Match[]): Match | null {
  if (candidates.length === 1) return candidates[0];
  if (candidates.length > 1) {
    return candidates.find((match) => match.activo) ?? candidates[0];
  }
  return null;
}

/**
 * Empareja por códigos FIFA del Excel (team_a_code / team_b_code).
 * TheSportsDB trae nombres en inglés; el calendario local usa códigos (CAN, BIH, KOR…).
 */
export function findMatchingMatch(event: SportsDbEvent, matches: Match[]): Match | null {
  const eventDateKeys = getEventDateKeys(event);
  const eventGroup = event.strGroup?.toUpperCase();

  const homeCode = getFifaCodeFromApiTeam(event.strHomeTeam);
  const awayCode = getFifaCodeFromApiTeam(event.strAwayTeam);

  if (homeCode && awayCode) {
    const eventCodeFingerprint = getPairCodeFingerprint(homeCode, awayCode);
    const byCode = matches.filter((match) => {
      if (!match.team_a_code || !match.team_b_code) return false;
      if (!matchesScheduleCriteria(match, eventDateKeys, eventGroup)) return false;

      const matchFingerprint = getPairCodeFingerprint(match.team_a_code, match.team_b_code);
      return matchFingerprint === eventCodeFingerprint;
    });

    const codeMatch = pickBestCandidate(byCode);
    if (codeMatch) return codeMatch;
  }

  return null;
}

export function mapEventScoresToMatch(
  event: SportsDbEvent,
  match: Match
): { scoreA: number; scoreB: number } | null {
  const scoreHome = parseScore(event.intHomeScore);
  const scoreAway = parseScore(event.intAwayScore);
  if (scoreHome === null || scoreAway === null) return null;

  const homeCode = getFifaCodeFromApiTeam(event.strHomeTeam);
  const matchHomeCode = match.team_a_code?.toUpperCase();

  if (homeCode && matchHomeCode && homeCode === matchHomeCode) {
    return { scoreA: scoreHome, scoreB: scoreAway };
  }

  if (homeCode && matchHomeCode && homeCode !== matchHomeCode) {
    return { scoreA: scoreAway, scoreB: scoreHome };
  }

  return { scoreA: scoreHome, scoreB: scoreAway };
}

/**
 * Solo true cuando la API trae un resultado oficial completo.
 * Si falta el evento, el marcador o el status no es final → false (se omite, no se toca Firestore).
 * Un 0-0 real sí cuenta: la API debe enviar explícitamente "0" en ambos lados con status FT/AET/PEN.
 */
export function hasOfficialResult(event: SportsDbEvent): boolean {
  if (!FINISHED_STATUSES.has(event.strStatus)) return false;

  const scoreHome = parseScore(event.intHomeScore);
  const scoreAway = parseScore(event.intAwayScore);

  return scoreHome !== null && scoreAway !== null;
}

async function fetchEvents(url: string): Promise<SportsDbEvent[]> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`TheSportsDB respondió con status ${response.status}`);
  }

  const data = (await response.json()) as { events?: SportsDbEvent[] | string };
  if (!Array.isArray(data.events)) {
    return [];
  }

  return data.events;
}

/** Consulta el endpoint oficial de TheSportsDB para el Mundial FIFA. */
export async function fetchWorldCup2026Events(): Promise<SportsDbEvent[]> {
  const events = await fetchEvents(WORLD_CUP_EVENTS_URL);
  return events.filter((event) => event.strSeason === MUNDIAL_SEASON);
}

/**
 * Cruza eventos de la API con partidos locales.
 * Solo propone actualizar partidos con resultado oficial en la respuesta.
 * Los partidos locales sin coincidencia o sin marcador en la API no se modifican.
 */
export function syncWorldCupResultsFromEvents(
  events: SportsDbEvent[],
  matches: Match[]
): SyncWorldCupResultsSummary {
  const updated: SyncMatchResult[] = [];
  const skipped: { event: SportsDbEvent; reason: string }[] = [];
  const unmatchedEvents: SportsDbEvent[] = [];
  const officialResults: SyncResultPreviewItem[] = [];
  const updatedMatchIds = new Set<string>();

  events.forEach((event) => {
    if (!hasOfficialResult(event)) {
      skipped.push({ event, reason: 'Sin resultado oficial en la API (se deja abierto)' });
      return;
    }

    const scoreHome = parseScore(event.intHomeScore);
    const scoreAway = parseScore(event.intAwayScore);
    const match = findMatchingMatch(event, matches);

    if (!match) {
      unmatchedEvents.push(event);
      officialResults.push({
        event,
        match: null,
        scoreA: scoreHome,
        scoreB: scoreAway,
        status: 'unmatched',
        statusLabel: 'Sin coincidencia local',
      });
      return;
    }

    const scores = mapEventScoresToMatch(event, match);
    if (!scores) {
      skipped.push({ event, reason: 'Marcador inválido en la API' });
      officialResults.push({
        event,
        match,
        scoreA: null,
        scoreB: null,
        status: 'invalid',
        statusLabel: 'Marcador inválido',
      });
      return;
    }

    const alreadySaved =
      match.score_a === scores.scoreA && match.score_b === scores.scoreB;

    officialResults.push({
      event,
      match,
      scoreA: scores.scoreA,
      scoreB: scores.scoreB,
      status: alreadySaved ? 'already_saved' : 'pending',
      statusLabel: alreadySaved ? 'Ya guardado' : 'Nuevo',
    });

    if (alreadySaved) {
      skipped.push({ event, reason: 'El resultado ya estaba guardado' });
      return;
    }

    if (updatedMatchIds.has(match.id)) {
      skipped.push({ event, reason: 'Partido ya sincronizado en esta corrida' });
      return;
    }

    updatedMatchIds.add(match.id);
    updated.push({ match, ...scores, event });
  });

  return { updated, skipped, unmatchedEvents, officialResults };
}
